import { AssignmentBudgetChartModel } from "@app/data/engine.ts";
import { useAssignmentBudgetChartsData } from "@app/data-hooks/use-assignment-budget-charts-data.ts";
import { formatUsd } from "@app/helpers/money-helper.ts";
import { BarChart, BarSeriesType } from "@mui/x-charts";
import { QueryStatusRouter } from "bi-lib";
import { useMemo } from "react";

interface ChartData {
    phase: string;
    category: string;
    budgetUsed: number[];
    budgetTotal: number[];
}

interface StaffData {
    name: string;
    isActive: boolean;
}

interface Props {
    assignmentId: number;
    staffIds?: number[];
}

export function AssignmentBudgetByStaffChart({ assignmentId, staffIds }: Props) {
    const { chartData } = useAssignmentBudgetChartsData({ assignmentId, staffIds });

    const staffOrder: StaffData[] = useMemo(() => {
        if (chartData.data == undefined || chartData.data.length == 0) {
            return [];
        }
        return chartData.data.reduce<StaffData[]>((acc, x) => {
            if (acc.some((y) => y.name == x.staff_name)) {
                return acc;
            }
            acc.push({ name: x.staff_name!, isActive: x.is_active ?? false });
            return acc;
        }, []);
    }, [chartData.data]);

    const formattedData = useMemo(() => {
        if (chartData.data == undefined || chartData.data.length == 0) {
            return [];
        }

        const sortedUniquePhases = chartData.data
            .reduce<AssignmentBudgetChartModel[]>((acc, x) => {
                if (
                    x.category_description == null ||
                    x.phase_description == null ||
                    acc.some(
                        (y) =>
                            y.phase_description == x.phase_description &&
                            y.category_description == x.category_description,
                    )
                ) {
                    return acc;
                }
                acc.push(x);
                return acc;
            }, [])
            .sort((a, b) => {
                if (a.phase_sort_order == b.phase_sort_order) {
                    return a.category_sort_order! - b.category_sort_order!;
                }
                return a.phase_sort_order! - b.phase_sort_order!;
            });

        const data: ChartData[] = [];
        for (const phase of sortedUniquePhases) {
            const row: ChartData = {
                phase: phase.phase_description!,
                category: phase.category_description!,
                budgetUsed: [],
                budgetTotal: [],
            };
            for (const staff of staffOrder) {
                const staffMatch = chartData.data.find(
                    (x) =>
                        x.staff_name == staff.name &&
                        x.phase_description == phase.phase_description &&
                        x.category_description == phase.category_description,
                );
                row.budgetUsed.push(staffMatch?.budget_used ?? 0);
                row.budgetTotal.push(staffMatch?.total_budget ?? 0);
            }
            data.push(row);
        }

        return data;
    }, [chartData.data, staffOrder]);

    const seriesData = useMemo(() => {
        if (formattedData.length == 0) {
            return [];
        }

        return formattedData.reduce<BarSeriesType[]>((acc, x, i) => {
            acc[i] = {
                type: "bar",
                data: x.budgetTotal,
                label: `Budget: ${x.phase} - ${x.category}`,
                stack: "Budget",
                valueFormatter: (value) => formatUsd(value ?? 0),
            };
            acc[i + formattedData.length] = {
                type: "bar",
                data: x.budgetUsed,
                label: `Used: ${x.phase} - ${x.category}`,
                stack: x.category,
                valueFormatter: (value) => formatUsd(value ?? 0),
            };
            return acc;
        }, []);
    }, [formattedData]);

    return (
        <QueryStatusRouter queryHook={chartData}>
            <BarChart
                xAxis={[{ scaleType: "band", data: staffOrder.map((x) => `${x.name}${!x.isActive ? " (I)" : ""}`) }]}
                series={seriesData}
                height={500}
                slotProps={{ legend: { hidden: true } }}
            />
        </QueryStatusRouter>
    );
}
